import { defineStore } from 'pinia'
import config from '@/config'
import { lowPriorityAsyncCall } from '@/utils'
import { ev } from '@/consts'
import * as api from '@/api'
import { USERINFO_KEY } from '@/consts'
import { SYSTEM_ACTIVITY_LIST, ACTIVITY_DATA } from '@/consts'
import { params, isInIframe, isInAndroidApp } from '@/env'

// import intersection from 'lodash-es/intersection'
// import { usePreferredLanguages } from '@vueuse/core';
import { eShowType, eCategoryIcon } from '@/types/index'
import type {
  iUserInfo,
  iWallet,
  tCurrency,
  tLang,
  tTheme,
  iGameCategory,
  iGame,
  iRoom,
  tActivityType,
  iActivity,
  // iPromocaoItem,
  iHomeBannersAndTexts,
  iServicoItem,
  iMerchantConfig,
  iUserDetail,
} from '@/types/index'

export const useUserInfo = defineStore(
  'userInfo',
  () => {
    const userInfo = ref<iUserInfo | null>(null)
    
    const userDetail = ref<iUserDetail | null>(null)

    const updateUserInfo = async (info?: Partial<iUserInfo>) => {
      if (userInfo.value) Object.assign(userInfo.value, info)
    }

    const updateUserDetail = async () => {
      const { code, data } = await api.getUserDetail()
      if (code === 1 && data) {
        userDetail.value = data
        if (isLoggedin.value) {
          Object.keys(userInfo.value!).forEach((key) => {
            if (key in userDetail.value!) {
              userInfo.value![key] = userDetail.value![key]
            }
          })
        }
      }
    }

    const isLoggedin = computed(() => !!userInfo.value?.token)

    const login = (info: iUserInfo, token: string) => {
      userInfo.value = info
      userInfo.value.token = token
      if (isInIframe) {
        window.parent.postMessage(
          // 通知主PWA登录成功
          {
            type: 'LOGIN',
            payload: {
              userInfo: JSON.stringify(userInfo.value),
            },
          },
          '*',
        )
      }

      if (isInAndroidApp) {
        window.jsBridge.postMessage(
          'setSession',
          JSON.stringify(userInfo.value),
        )
      }

      ev.emit('login')
    }

    const logout = () => {
      userInfo.value = null
      delete localStorage[USERINFO_KEY]
      if (isInIframe) {
        window.parent.postMessage(
          {
            type: 'LOGOUT',
          },
          '*',
        )
      }

      if (isInAndroidApp) {
        window.jsBridge.postMessage('setSession', '')
      }
      ev.emit('logout')
    }

    return { userInfo, updateUserInfo, isLoggedin, logout, login, userDetail, updateUserDetail }
  },
  {
    persist: {
      key: USERINFO_KEY,
    },
  },
)

export const useWallet = defineStore('wallet', () => {
  const wallet = ref<iWallet>({} as iWallet)

  const getWallet = async () => {
    const currency = (useUserInfo().userInfo?.currency as tCurrency) || 'BRL'
    const { code, data } = await api.getWallet({ currency })
    if (code === 1 && data) {
      wallet.value = data
    }
  }

  const updateWallet = async (data: Partial<iWallet>) => {
    Object.assign(wallet.value, data)
  }

  const reset = async () => {
    wallet.value = {} as iWallet
  }

  const init = async () => {
    if (useUserInfo().isLoggedin) await getWallet()
  }

  return { wallet, updateWallet, reset, init }
})

export const useSystemInfo = defineStore('systemInfo', () => {
  const theme = ref<tTheme>('')
  const language = ref<tLang>('pt')
  const languages = ref<tLang[]>(['pt'])
  const langStr = computed(() => {
    const lang = language.value
    if (lang === 'en') return 'en-US'
    if (lang === 'pt') return 'pt-BR'
    return lang
  })

  const imageBaseUrl = ref('')
  const merchantConfig = ref<iMerchantConfig | null>(null)

  const init = async () => {
    const { code, data } = await api
      .getMerchantConfig()
      .catch(() => ({ code: 0, data: null }))
    if (code === 1 && data) {
      imageBaseUrl.value = data.accessUrl
      merchantConfig.value = data.merchant

      const tpl = params.get('tpl') as tTheme
      const themeAlias = {
        dark_green: 'theme_a',
        dark_gold: 'theme_b',
      }
      if (tpl) {
        theme.value = tpl in themeAlias ? themeAlias[tpl] : 'theme_a'
      } else {
        const s = data.merchant.clientSkin?.toLowerCase()
        theme.value = s in themeAlias ? themeAlias[s] : 'theme_a'
      }

      const lang = params.get('lang') as tLang
      if (lang) {
        language.value = lang
        languages.value = [lang]
      } else {
        // const preferedLang = intersection(usePreferredLanguages().value, data.merchant.languages);
        const preferedLang = data.merchant?.languages || []
        if (preferedLang.length) {
          language.value = preferedLang[0]
          languages.value = preferedLang
        } else if (data.merchant.languages?.length) {
          language.value = data.merchant.languages[0]
          languages.value = data.merchant.languages
        }
      }
    } else {
      throw new Error('failed to get system info')
    }
  }

  function setLanguage(val: tLang) {
    language.value = val
  }

  return {
    theme,
    language,
    langStr,
    imageBaseUrl,
    merchantConfig,
    setLanguage,
    init,
    languages,
  }
})

export const useOpenningImage = defineStore('openningImage', () => {
  const openningImage = ref('')

  function updateOpenningImage() {
    openningImage.value =
      localStorage.getItem('loadingImage') || config.openningImage

    // 异步获取最新开屏图片, 下次打开时使用
    lowPriorityAsyncCall(() => {
      getOpenningImageFromServer()
    })
  }

  async function getOpenningImageFromServer() {
    let src = ''
    const { code, data } = await api.getOpening()
    if (code === 1 && data) src = data.picture

    lowPriorityAsyncCall(() => {
      if (localStorage.getItem('loadingImage') !== src) new Image().src = src
      localStorage.setItem('loadingImage', src)
    })

    return src
  }

  const init = async () => {
    await updateOpenningImage()
  }

  return { openningImage, updateOpenningImage, init }
})

export const useGameCategory = defineStore('gameCategory', () => {
  const list = ref<iGameCategory[]>([])
  const topList = computed(() =>
    list.value.filter(
      (item) =>
        item.showType === eShowType.Top || item.showType === eShowType.All,
    ),
  )
  const leftList = computed(() =>
    list.value.filter(
      (item) =>
        item.showType === eShowType.Left || item.showType === eShowType.All,
    ),
  )

  async function updateGameCategory() {
    const { code, data } = await api.getGameCategorys()
    if (code === 1 && data) {
      list.value = data.map((item) => {
        item.defaultIcon =
          eCategoryIcon[item.name as keyof typeof eCategoryIcon]
        return item
      })
    }
  }

  const init = async () => {
    await updateGameCategory()
  }

  return { list, topList, leftList, updateGameCategory, init }
})

export const useFavGamelist = defineStore('favGamelist', () => {
  const list = ref<string[]>([])

  async function updateList() {
    const { code, data } = await api.queryFavoriteGameList()
    if (code === 1 && data) {
      list.value = data
    }
  }

  async function toggleFavorite(isFavorite: boolean, code: string) {
    try {
      if (isFavorite) {
        const { code: deleteCode } = await api.deleteFavoriteGame({ code })
        if (deleteCode === 1)
          list.value = list.value.filter((item) => item !== code)
      } else {
        const { code: createCode } = await api.createFavoriteGame({ code })
        if (createCode === 1) list.value.push(code)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const init = async () => {
    await updateList()
  }

  return { list, updateList, toggleFavorite, init }
})

export const useGame = defineStore('game', () => {
  const list = ref<iGame[]>([])

  async function updateList() {
    const { code, data } = await api.getGameListByCategory({})
    if (code === 1 && data) {
      list.value = data
    }
  }

  const init = async () => {
    await updateList()
  }

  return { list, updateList, init }
})

export const useRoom = defineStore('room', () => {
  const list = ref<iRoom[]>([])

  async function updateRoom() {
    const { code, data } = await api.getRoomList({})
    if (code === 1 && data) {
      list.value = data
    }
  }

  const init = async () => {
    await updateRoom()
  }

  return { list, init, updateRoom }
})

export const useActivity = defineStore('activity', () => {
  const list = ref<iActivity[]>([])
  const listOfType = computed(
    () =>
      (type: tActivityType | 'HOME'): iActivity[] => {
        if (type === 'HOME')
          return list.value.filter((item) =>
            SYSTEM_ACTIVITY_LIST.includes(item.activityType),
          )
        return list.value.filter((item) => item.activityType === type)
      },
  )

  const activityForType = computed(() => (type: tActivityType): iActivity => {
    return (
      list.value.find((item) => item.activityType === type) || ({} as iActivity)
    )
  })

  const updateActivity = async () => {
    let activityList: iActivity[] = []
    try {
      let { code, data } = await api.getActivityDetail({
        activityId: 'All',
      })
      if (code === 1 && data && data.length) {
        activityList = data.map((i: any) => {
          const ret = { ...i } as iActivity
          if (i.activityType === 'CheckIn') {
            ret.configJson = JSON.stringify(ACTIVITY_DATA.CheckIn)
          } else if (i.activityType === 'Task') {
            ret.configJson = JSON.stringify(ACTIVITY_DATA.Task)
          } else if (i.activityType === 'Wheel' && !ret.configJson) {
            ret.configJson = JSON.stringify(ACTIVITY_DATA.Wheel)
          }
          return ret
        })
      }
    } catch (error) {}

    list.value = activityList
  }

  const init = async () => {
    if (!list.value.length) updateActivity()
  }

  return { list, listOfType, activityForType, updateActivity, init }
})

const handleListItem = (li: any) => {
  const { language, theme } = useSystemInfo()
  const item = {
    ...ACTIVITY_DATA[li.activityType],
    ...li,
    bannerUrl: li.bannerUrl.replace(
      'local://',
      'local://' + theme + '/' + language + '/',
    ),
    showRedPoint: li.rewardStatus === 'Awarded',
  }

  if (SYSTEM_ACTIVITY_LIST.includes(item.activityType)) {
    item.link = '/promocao/' + item.linkName
  } else {
    item.link = '/' + item.linkName
  }

  return item
}

const getPromocaoList = async () => {
  const fn = useUserInfo().isLoggedin
    ? api.getPromocaoList
    : api.getPromocaoListNotLogin
  const { code, data } = await fn({ language: useSystemInfo().language }).catch(
    () => ({ code: 0, data: null }),
  )
  const res: {
    activityList: iActivity[]
    systemList: iActivity[]
    customList: iActivity[]
    typedList: { type: string; configList: iActivity[] }[]
  } = {
    activityList: [],
    systemList: [],
    customList: [],
    typedList: [],
  }

  if (code === 1 && data) {
    res.activityList =
      data.multiple
        .at(-1)
        .configList?.filter((item: any) => item.status === 'Active')
        .map(handleListItem) || []
    res.systemList = data.single
      .filter((item: any) => item.config?.status === 'Active')
      .map((item: any) => handleListItem(item.config))
    res.typedList = data.multiple.slice(0, -1).map((item: any) => {
      item.configList =
        item.configList
          ?.filter((item: any) => item.status === 'Active')
          .map(handleListItem) || []
      return item
    })

    res.customList = res.typedList.flatMap((item) => item.configList)
  }

  return res
}

export const useActivityList = defineStore('activityList', () => {
  const activityList = ref<iActivity[]>([])
  const systemList = ref<iActivity[]>([])
  const customList = ref<iActivity[]>([])
  const typedList = ref<{ type: string; configList: iActivity[] }[]>([])
  const updatePromocaoList = async () => {
    const {
      systemList: systemListData,
      customList: customListData,
      typedList: typedListData,
      activityList: activityListData,
    } = await getPromocaoList()

    activityList.value = activityListData
    systemList.value = systemListData
    customList.value = customListData
    typedList.value = typedListData
  }

  const getActivityById = (id: string) =>
    activityList.value.find((item) => item.activityId === id)

  const init = async () => {
    await updatePromocaoList()
  }

  return {
    activityList,
    systemList,
    customList,
    typedList,
    updatePromocaoList,
    init,
    getActivityById,
  }
})

// export const usePromocao = defineStore('promocao', () => {
//   const list = ref<iPromocaoItem[]>([])
//   const unread = ref<string[]>([])

//   const getUnread = () => {
//     const unreadPromocoes = localStorage.getItem('unreadPromocoes')
//     if (unreadPromocoes) {
//       try {
//         unread.value = JSON.parse(unreadPromocoes)
//       } catch (error) {
//         unread.value = []
//       }
//     }
//   }

//   const markRead = (id: string) => {
//     const idx = unread.value.findIndex(i => i === id)
//     if (idx >= 0) {
//       unread.value.splice(idx, 1)
//       localStorage.setItem('unreadPromocoes', JSON.stringify(unread.value))
//     }
//   }

//   const getPromocaoList = async () => {
//     const { code, data } = await api.getPromocaoList({language: useSystemInfo().language})
//     if (code === 1 && data) {
//       return data
//     }
//     return []
//   }

//   const updatePromocaoList = async () => {
//     const activities = await getPromocaoList()
//     let unreadActivities: string[] = []
//     try {
//       unreadActivities = JSON.parse(localStorage.getItem('unreadPromocoes') || '[]')
//     } catch (error) {}
//     const newList = (activities || []).map((item) => {
//       const add = {
//         ...item,
//         showRedPoint: unreadActivities.includes(item.id.toString()) || false
//       }
//       return add
//     })
//     list.value = newList
//   }
//   const init = async () => {
//     getUnread()
//     await updatePromocaoList()
//   }

//   return { list, init, markRead, updatePromocaoList }
// })

export const useHomeBannersAndTexts = defineStore('homeBannersAndTexts', () => {
  const config = ref<iHomeBannersAndTexts>({
    suspendIcon: null,
    carouselMessage: [],
    topDownload: null,
    bottomDownload: null,
    bottomText: null,
    bigBanner: [],
    smallBanner: [],
    popupImage: [],
    socialMedia: [],
    webTitle: '',
    webDescription: '',
    webLogo: '',
    webIcon: '',
    pwaTitle: '',
    pwaShortTitle: '',
    pwaIcon: '',
    webPageIcon: '',
  })

  const suspendIcon = computed(() => config.value.suspendIcon)
  const carouselMessage = computed(() => config.value.carouselMessage)
  const topDownload = computed(() => config.value.topDownload)
  const bottomDownload = computed(() => config.value.bottomDownload)
  const bottomText = computed(() => config.value.bottomText)
  const bigBanner = computed(() => config.value.bigBanner)
  const smallBanner = computed(() => config.value.smallBanner)
  const popupImage = computed(() => config.value.popupImage)
  const socialMedia = computed(() => config.value.socialMedia)
  const pwaConfig = computed(() => ({
    webTitle: config.value.webTitle,
    webDescription: config.value.webDescription,
    webLogo: config.value.webLogo,
    webIcon: config.value.webIcon,
    webPageIcon: config.value.webPageIcon,
    pwaTitle: config.value.pwaTitle,
    pwaShortTitle: config.value.pwaShortTitle,
    pwaIcon: config.value.pwaIcon,
  }))

  const updateConfig = async () => {
    const { code, data } = await api.getHomeConfigs()
    if (code === 1 && data) {
      const enabled = {} as iHomeBannersAndTexts

      Object.entries(data as iHomeBannersAndTexts).forEach(([key, value]) => {
        if (key === 'socialMedia') {
          enabled[key] = value
        } else if (Array.isArray(value)) {
          enabled[key] = value.filter((item) => item.state === 'Enabled')
        } else if (typeof value == 'string') {
          enabled[key] = value
        } else {
          enabled[key] = value.state === 'Enabled' ? value : null
        }
      })

      Object.assign(config.value, enabled)
    }
  }

  const init = async () => {
    await updateConfig()
  }

  return {
    suspendIcon,
    carouselMessage,
    topDownload,
    bottomDownload,
    bottomText,
    bigBanner,
    smallBanner,
    popupImage,
    socialMedia,
    pwaConfig,
    init,
  }
})

export const useServico = defineStore('servico', () => {
  const list = ref<iServicoItem[]>([])

  const getServico = async () => {
    const { code, data } = await api.getServicoConfig()
    if (code === 1 && data) {
      return data.filter((item) => item.status == 'Online')
    }

    return []
  }

  const updateList = async () => {
    list.value = await getServico()
  }

  const init = async () => {
    await updateList()
  }

  return { list, updateList, init }
})
